import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { DEMO_USERS } from '../../contexts/DemoContext';
import { collection, getDocs, doc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getAllRoles, getRoleDisplayName } from '../../constants/userRoles';
import { logAudit, AUDIT_ACTIONS } from '../../services/auditService';

const IS_DEMO = process.env.REACT_APP_DEMO_MODE === 'true';

const UserManagement = () => {
  const { userProfile } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadUsers();
  }, [userProfile]);

  const loadUsers = async () => {
    if (!userProfile?.practiceId) return;
    try {
      setLoading(true);

      if (IS_DEMO) {
        setUsers([...DEMO_USERS]);
        setLoading(false);
        return;
      }

      const usersRef = collection(db, 'practices', userProfile.practiceId, 'users');
      const snapshot = await getDocs(usersRef);
      const usersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersData);
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    if (IS_DEMO) {
      setUsers(prev => prev.map(u =>
        u.id === userId ? { ...u, role: newRole } : u
      ));
      setEditingUser(null);
      return;
    }

    try {
      const userRef = doc(db, 'practices', userProfile.practiceId, 'users', userId);
      await updateDoc(userRef, {
        role: newRole,
        updatedAt: serverTimestamp()
      });

      await logAudit({
        practiceId: userProfile.practiceId,
        userId: userProfile.id,
        action: AUDIT_ACTIONS.UPDATE,
        resourceType: 'user',
        resourceId: userId,
        details: { field: 'role', newValue: newRole }
      });

      setUsers(prev => prev.map(u =>
        u.id === userId ? { ...u, role: newRole } : u
      ));
      setEditingUser(null);
    } catch (err) {
      console.error('Error updating role:', err);
      setError('Failed to update user role');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (IS_DEMO) {
      setUsers(prev => prev.filter(u => u.id !== userId));
      setShowDeleteModal(null);
      return;
    }

    try {
      const userRef = doc(db, 'practices', userProfile.practiceId, 'users', userId);
      await deleteDoc(userRef);

      await logAudit({
        practiceId: userProfile.practiceId,
        userId: userProfile.id,
        action: AUDIT_ACTIONS.DELETE,
        resourceType: 'user',
        resourceId: userId,
      });

      setUsers(prev => prev.filter(u => u.id !== userId));
      setShowDeleteModal(null);
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Failed to delete user');
    }
  };

  const filteredUsers = users.filter(u =>
    (u.displayName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (date) => {
    if (!date) return 'N/A';
    if (date.toDate) return date.toDate().toLocaleDateString();
    return new Date(date).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage team members and their access permissions
          </p>
        </div>
        <div className="text-sm text-gray-500">
          {users.length} team member{users.length !== 1 ? 's' : ''}
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-800">{error}</p>
          <button onClick={() => setError('')} className="text-xs text-red-600 hover:text-red-800 mt-1">
            Dismiss
          </button>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <svg className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Users Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Login</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredUsers.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-9 w-9 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-blue-700 font-medium text-sm">
                        {(user.displayName || user.email || '?').charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">{user.displayName}</p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {editingUser === user.id ? (
                    <select
                      defaultValue={user.role}
                      onChange={(e) => handleRoleChange(user.id, e.target.value)}
                      onBlur={() => setEditingUser(null)}
                      autoFocus
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      {getAllRoles().map(role => (
                        <option key={role.value} value={role.value}>{role.displayName}</option>
                      ))}
                    </select>
                  ) : (
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer ${
                        user.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                        user.role === 'doctor' ? 'bg-blue-100 text-blue-800' :
                        'bg-green-100 text-green-800'
                      }`}
                      onClick={() => user.id !== userProfile.id && setEditingUser(user.id)}
                    >
                      {getRoleDisplayName(user.role)}
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(user.createdAt)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(user.lastLogin)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                  {user.id !== userProfile.id && (
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => setEditingUser(user.id)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Edit Role
                      </button>
                      <button
                        onClick={() => setShowDeleteModal(user.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                  {user.id === userProfile.id && (
                    <span className="text-xs text-gray-400">(You)</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredUsers.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            <p>No users found</p>
          </div>
        )}
      </div>

      {/* Role Permissions Reference */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Role Permissions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              role: 'Doctor',
              color: 'blue',
              perms: ['Full patient access', 'Treatment planning', 'Photo editing & AI tools', 'View team members']
            },
            {
              role: 'Staff',
              color: 'green',
              perms: ['Patient management', 'Case management', 'Photo upload & editing', 'No delete access']
            },
            {
              role: 'Administrator',
              color: 'purple',
              perms: ['Full system access', 'User management', 'Audit log access', 'Practice settings']
            }
          ].map(({ role, color, perms }) => (
            <div key={role} className={`border border-${color}-200 rounded-lg p-4`}>
              <h4 className={`text-sm font-medium text-${color}-700 mb-2`}>{role}</h4>
              <ul className="text-xs text-gray-600 space-y-1">
                {perms.map((p, i) => (
                  <li key={i} className="flex items-center">
                    <svg className={`w-3 h-3 mr-1.5 text-${color}-500`} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowDeleteModal(null)}></div>
            <div className="relative bg-white rounded-lg p-6 max-w-md w-full">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Remove User</h3>
              <p className="text-sm text-gray-500 mb-4">
                Are you sure you want to remove this user from your practice? They will lose access to all patient data and records.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteModal(null)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteUser(showDeleteModal)}
                  className="px-4 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700"
                >
                  Remove User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
